import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import ClampText from './ClampText';
import Modal from './Modal';
import useInView from '../hooks/useInView';
import '../css/ProjectCard.css';

const TAG_CATEGORY_BY_NAME = new Map([
  ['react', 'frontend'],
  ['next.js', 'frontend'],
  ['javascript', 'frontend'],
  ['typescript', 'frontend'],
  ['css', 'frontend'],
  ['html', 'frontend'],
  ['bootstrap', 'frontend'],
  ['tailwind css', 'frontend'],
  ['material-ui', 'frontend'],
  ['jquery', 'frontend'],
  ['jsx', 'frontend'],
  ['figma', 'frontend'],
  ['webrtc', 'frontend'],

  ['express', 'backend'],
  ['firebase', 'backend'],
  ['mongodb', 'backend'],
  ['mongoose', 'backend'],
  ['postgresql', 'backend'],
  ['stripe', 'backend'],
  ['openai realtime api', 'backend'],
  ['java', 'backend'],
  ['python', 'backend'],
  ['c', 'backend'],
  ['c言語', 'backend'],
  ['oop', 'backend'],
  ['オブジェクト指向', 'backend'],
  ['web assembly', 'backend'],
  ['emscripten', 'backend'],
  ['ウェブアセンブリ言語', 'backend'],
  ['javafx', 'backend'],
  ['x86 assembly', 'backend'],
  ['x86 アセンブリ言語', 'backend'],
  ['linux', 'backend'],

  ['amazon cognito', 'cloud'],
  ['aws elastic beanstalk', 'cloud'],
  ['alb', 'cloud'],
  ['render', 'cloud'],

  ['team', 'meta'],
  ['チーム開発', 'meta'],
  ['ペア開発', 'meta'],
  ['personal', 'meta'],
  ['personal project', 'meta'],
  ['個人開発', 'meta'],
  ['academic', 'meta'],
  ['アカデミック', 'meta'],
  ['hackathon', 'meta'],
  ['ハッカソン', 'meta'],
]);

function resolveImagePath(imagePath) {
  if (typeof imagePath !== 'string') {
    return '';
  }

  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  const normalizedPath = imagePath.replace(/^\/+/, '');
  return `${process.env.PUBLIC_URL}/${normalizedPath}`;
}

function getTagCategory(tag) {
  const normalizedTag = String(tag || '').trim().toLowerCase();
  if (TAG_CATEGORY_BY_NAME.has(normalizedTag)) {
    return TAG_CATEGORY_BY_NAME.get(normalizedTag);
  }

  // Fallback for newly added tags not yet in the exact mapping.
  if (
    normalizedTag.includes('cognito')
    || normalizedTag.includes('elastic beanstalk')
    || normalizedTag.includes('load balancer')
    || normalizedTag.includes('render')
    || normalizedTag === 'alb'
  ) {
    return 'cloud';
  }

  if (
    normalizedTag.includes('mongo')
    || normalizedTag.includes('postgres')
    || normalizedTag.includes('firestore')
    || normalizedTag.includes('firebase')
    || normalizedTag.includes('express')
    || normalizedTag.includes('stripe')
    || normalizedTag.includes('openai')
    || normalizedTag.includes('assembly')
  ) {
    return 'backend';
  }

  if (
    normalizedTag.includes('react')
    || normalizedTag.includes('next')
    || normalizedTag.includes('javascript')
    || normalizedTag.includes('typescript')
    || normalizedTag.includes('tailwind')
    || normalizedTag.includes('bootstrap')
    || normalizedTag.includes('webrtc')
    || normalizedTag.includes('css')
    || normalizedTag.includes('html')
  ) {
    return 'frontend';
  }

  return 'meta';
}

function ProjectCard({
  project,
  language,
  index,
  variant = 'compact',
  flip = false,
  activeTag = '',
  onTagClick = undefined,
}) {
  const [showModal, setShowModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [cardRef, isVisible] = useInView({ threshold: 0.08 });

  const title = language === 'ja' ? project.title_ja : project.title_en;
  const tags = (language === 'ja' ? project.tags_ja : project.tags_en) || [];
  const description = language === 'ja' ? project.description_ja : project.description_en;
  const isTagFilterable = typeof onTagClick === 'function';
  const resolvedImages = project.images.map((projectImage) => resolveImagePath(projectImage));

  const swiperA11y =
    language === 'ja'
      ? {
          prevSlideMessage: '前のスライド',
          nextSlideMessage: '次のスライド',
          containerMessage: '画像スライダー',
        }
      : undefined;

  const openModal = (imageIndex) => {
    setSelectedImageIndex(imageIndex);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleTagClick = (tag) => {
    if (!isTagFilterable) {
      return;
    }

    onTagClick(tag);
  };

  const classes = [
    'project-card',
    `project-card--${variant}`,
    flip ? 'project-card--flip' : '',
    'reveal',
    isVisible ? 'is-visible' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <article className={classes} ref={cardRef}>
      <div className="project-card__media">
        <Swiper
          modules={[A11y, Navigation, Pagination]}
          navigation
          pagination={{ type: 'fraction' }}
          a11y={swiperA11y}
          loop
          spaceBetween={14}
          className="project-swiper"
        >
          {resolvedImages.map((resolvedImage, imageIndex) => {
            return (
              <SwiperSlide key={`${project.id}-image-${imageIndex}`} className="project-slide">
                <img
                  src={resolvedImage}
                  alt={
                    language === 'ja'
                      ? `${title} スクリーンショット ${imageIndex + 1}`
                      : `${project.title_en} screenshot ${imageIndex + 1}`
                  }
                  className="project-slide__image"
                  loading="lazy"
                />
                <button
                  type="button"
                  onClick={() => openModal(imageIndex)}
                  className="project-slide__expand mono"
                >
                  {language === 'ja' ? '拡大' : 'Expand'}
                </button>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>

      {showModal && (
        <Modal
          images={resolvedImages}
          currentIndex={selectedImageIndex}
          onSelectImage={setSelectedImageIndex}
          onClose={closeModal}
          title={title}
          language={language}
        />
      )}

      <div className="project-card__body">
        <p className="project-card__index mono">PROJECT {String(index).padStart(2, '0')}</p>
        {/* Compact cards sit under the archive's own h3, so they head at h4. */}
        {variant === 'compact' ? (
          <h4 className="project-card__title">{title}</h4>
        ) : (
          <h3 className="project-card__title">{title}</h3>
        )}

        <div className="project-card__tags">
          {tags.map((tag) => {
            const isActiveTag = activeTag === tag;
            const tagClasses = [
              'tag',
              `tag--${getTagCategory(tag)}`,
              isTagFilterable ? 'tag--clickable' : '',
              isActiveTag ? 'is-active' : '',
            ]
              .filter(Boolean)
              .join(' ');

            if (!isTagFilterable) {
              return (
                <span key={`${project.id}-${tag}`} className={tagClasses}>
                  {tag}
                </span>
              );
            }

            return (
              <button
                key={`${project.id}-${tag}`}
                type="button"
                className={tagClasses}
                onClick={() => handleTagClick(tag)}
                aria-pressed={isActiveTag}
              >
                {tag}
              </button>
            );
          })}
        </div>

        <ClampText language={language}>
          <p className="project-card__description">{description}</p>
        </ClampText>

        <ul className="project-card__link-list" aria-label={language === 'ja' ? '関連リンク' : 'Links'}>
          {project.links.map((link, linkIndex) => (
            <li key={`${project.id}-link-${linkIndex}`}>
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="project-card__link"
              >
                <span>{language === 'ja' ? link.title_ja : link.title_en}</span>
                <span className="project-card__link-arrow" aria-hidden="true">
                  ↗
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}

export default ProjectCard;
